import React, { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { Save, Key, Mail, MessageSquare, Cloud, CreditCard, FileText, Plus, Edit, Trash2, Sun, CloudRain, Wind, Thermometer, Zap, Shield, Loader2, Send } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const DEFAULT_EMAIL_TEMPLATES = [
  { id: 'booking_confirmation', name: 'Booking Confirmation', subject: 'Your Reservation is Confirmed - {{confirmation_code}}', body: 'Dear {{guest_name}},\n\nYour reservation has been confirmed.\n\nConfirmation Code: {{confirmation_code}}\nSeat: {{seat_label}}\nDate: {{reservation_date}}\nAmount: {{amount}}\n\nThank you for choosing Acqualina Resort.', description: 'Sent when a reservation is confirmed', isDefault: true },
  { id: 'booking_reminder', name: 'Booking Reminder', subject: 'Reminder: Your Reservation Tomorrow', body: 'Dear {{guest_name}},\n\nThis is a reminder about your reservation tomorrow.\n\nConfirmation Code: {{confirmation_code}}\nSeat: {{seat_label}}\n\nWe look forward to seeing you!', description: 'Sent 24 hours before reservation', isDefault: true },
  { id: 'booking_cancelled', name: 'Booking Cancelled', subject: 'Your Reservation Has Been Cancelled', body: 'Dear {{guest_name}},\n\nYour reservation ({{confirmation_code}}) has been cancelled.\n\nIf you did not request this cancellation, please contact us immediately.', description: 'Sent when reservation is cancelled', isDefault: true },
  { id: 'payment_receipt', name: 'Payment Receipt', subject: 'Payment Receipt - {{amount}}', body: 'Dear {{guest_name}},\n\nThank you for your payment of {{amount}}.\n\nConfirmation Code: {{confirmation_code}}\nSeat: {{seat_label}}\nDate: {{reservation_date}}', description: 'Sent after successful payment', isDefault: true },
  { id: 'weather_alert', name: 'Weather Alert', subject: 'Weather Advisory for Your Reservation', body: 'Dear {{guest_name}},\n\nDue to weather conditions, your reservation may be affected. Please check with our staff upon arrival.\n\nConfirmation Code: {{confirmation_code}}', description: 'Sent when weather conditions may affect reservation', isDefault: true },
];

const DEFAULT_SMS_TEMPLATES = [
  { id: 'sms_confirmation', name: 'Booking Confirmation', body: 'Acqualina: Reservation confirmed! Code: {{confirmation_code}}, Seat: {{seat_label}}, Date: {{reservation_date}}', description: 'Short confirmation message', isDefault: true },
  { id: 'sms_reminder', name: 'Booking Reminder', body: 'Acqualina: Reminder - your reservation {{confirmation_code}} is tomorrow! Seat: {{seat_label}}', description: 'Day-before reminder', isDefault: true },
  { id: 'sms_checkin', name: 'Check-in Ready', body: 'Acqualina: Your seat {{seat_label}} is ready! Present code {{confirmation_code}} at check-in.', description: 'When seat is ready', isDefault: true },
  { id: 'sms_weather', name: 'Weather Alert', body: 'Acqualina Weather Alert: Conditions may affect your reservation. Check with staff upon arrival.', description: 'Weather warning', isDefault: true },
];

export default function IntegrationSettings() {
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    ohip_url: '',
    ohip_client_id: '',
    ohip_client_secret: '',
    ohip_application_key: '',
    stripe_api_key: '',
    twilio_account_sid: '',
    twilio_auth_token: '',
    twilio_phone_number: '',
    sms_enabled: false,
    send_confirmation_sms: true,
    send_reminder_sms: true,
    reminder_hours_before: 24,
    smtp2go_api_key: '',
    smtp2go_sender_email: '',
    smtp2go_sender_name: '',
    weather_enabled: false,
    weather_zip_code: '33160',
    weather_latitude: '',
    weather_longitude: '',
    weather_check_interval_minutes: 60,
    weather_alerts_cutoff_time: '18:00'
  });
  
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [weatherAlerts, setWeatherAlerts] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templateType, setTemplateType] = useState('email');
  const [templateForm, setTemplateForm] = useState({
    id: '',
    name: '',
    subject: '',
    body: '',
    description: ''
  });
  const [weatherAlertDialogOpen, setWeatherAlertDialogOpen] = useState(false);
  const [editingWeatherAlert, setEditingWeatherAlert] = useState(null);
  const [weatherAlertForm, setWeatherAlertForm] = useState({
    alert_type: 'wind',
    threshold_value: 15,
    threshold_unit: 'mph',
    comparison: 'gte',
    message: '',
    check_interval_minutes: 30,
    cooldown_minutes: 120,
    active: true
  });
  const [legalPages, setLegalPages] = useState({
    sms_terms: { title: '', content: '' },
    privacy_policy: { title: '', content: '' }
  });
  const [editingLegalPage, setEditingLegalPage] = useState(null);
  const [legalPageDialogOpen, setLegalPageDialogOpen] = useState(false);
  const [testSmsPhone, setTestSmsPhone] = useState('');
  const [testAlertPhone, setTestAlertPhone] = useState('');
  const [testAlertMessage, setTestAlertMessage] = useState('Weather Alert: This is a test message from Acqualina Resort.');
  const [testTemplateDialogOpen, setTestTemplateDialogOpen] = useState(false);
  const [testingTemplate, setTestingTemplate] = useState(null);
  const [testRecipient, setTestRecipient] = useState('');

  useEffect(() => {
    fetchProperties();
  }, []);

  useEffect(() => {
    if (selectedProperty) {
      fetchSettings();
      fetchTemplates();
      fetchWeatherAlerts();
      fetchLegalPages();
    }
  }, [selectedProperty]);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API_URL}/properties`);
      setProperties(response.data);
      if (response.data.length > 0) {
        setSelectedProperty(response.data[0].id);
      }
    } catch (error) {
      toast.error('Failed to load properties');
    }
  };

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/settings/integrations/${selectedProperty}`);
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      // Settings may not exist yet, that's okay
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/templates?property_id=${selectedProperty}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const emailData = response.data.email_templates || [];
      const smsData = response.data.sms_templates || [];
      
      // Merge default templates with custom templates
      // Custom templates override defaults with the same ID
      const customEmailIds = new Set(emailData.map(t => t.id));
      const mergedEmailTemplates = [
        ...DEFAULT_EMAIL_TEMPLATES.filter(t => !customEmailIds.has(t.id)),
        ...emailData
      ];
      
      const customSmsIds = new Set(smsData.map(t => t.id));
      const mergedSmsTemplates = [
        ...DEFAULT_SMS_TEMPLATES.filter(t => !customSmsIds.has(t.id)),
        ...smsData
      ];
      
      setEmailTemplates(mergedEmailTemplates);
      setSmsTemplates(mergedSmsTemplates);
    } catch (error) {
      // Use defaults if API fails
      setEmailTemplates(DEFAULT_EMAIL_TEMPLATES);
      setSmsTemplates(DEFAULT_SMS_TEMPLATES);
    }
  };

  const fetchWeatherAlerts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/weather-alerts?property_id=${selectedProperty}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWeatherAlerts(response.data);
    } catch (error) {
      setWeatherAlerts([]);
    }
  };

  const fetchLegalPages = async () => {
    try {
      const [smsTerms, privacyPolicy] = await Promise.all([
        axios.get(`${API_URL}/legal/sms_terms?property_id=${selectedProperty}`),
        axios.get(`${API_URL}/legal/privacy_policy?property_id=${selectedProperty}`)
      ]);
      setLegalPages({
        sms_terms: smsTerms.data,
        privacy_policy: privacyPolicy.data
      });
    } catch (error) {
      // Use defaults
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(`${API_URL}/settings/integrations/${selectedProperty}`, settings);
      toast.success('Settings saved successfully');
      fetchSettings();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings({ ...settings, [field]: value });
  };

  const openTemplateDialog = (template, type) => {
    setTemplateType(type);
    if (template) {
      setEditingTemplate(template);
      setTemplateForm({
        id: template.id,
        name: template.name,
        subject: template.subject || '',
        body: template.body || '',
        description: template.description || ''
      });
    } else {
      setEditingTemplate(null);
      setTemplateForm({ id: '', name: '', subject: '', body: '', description: '' });
    }
    setTemplateDialogOpen(true);
  };

  const handleSaveTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const data = {
        property_id: selectedProperty,
        template_type: templateType,
        name: templateForm.name,
        subject: templateForm.subject,
        body: templateForm.body,
        description: templateForm.description
      };
      
      // Check if this is a default template (isDefault flag or default ID patterns)
      const isDefaultTemplate = editingTemplate?.isDefault || 
        (editingTemplate?.id && (editingTemplate.id.startsWith('sms_') || ['booking_confirmation', 'booking_reminder', 'booking_cancelled', 'payment_receipt', 'weather_alert'].includes(editingTemplate.id)));
      
      if (editingTemplate && !isDefaultTemplate) {
        // Update existing custom template
        await axios.put(`${API_URL}/templates/${editingTemplate.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Template updated');
      } else {
        // Create new template (either new or copying from default)
        await axios.post(`${API_URL}/templates`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(isDefaultTemplate ? 'Template saved (copy created from default)' : 'Template created');
      }
      
      setTemplateDialogOpen(false);
      fetchTemplates();
    } catch (error) {
      console.error('Failed to save template:', error);
      toast.error('Failed to save template: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Test template handler
  const openTestTemplateDialog = (template, type) => {
    setTestingTemplate({ ...template, type });
    setTestRecipient('');
    setTestTemplateDialogOpen(true);
  };

  const handleSendTestTemplate = async () => {
    if (!testRecipient) {
      toast.error(testingTemplate.type === 'email' ? 'Please enter an email address' : 'Please enter a phone number');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // Replace template variables with sample data
      const sampleData = {
        '{{guest_name}}': 'Test Guest',
        '{{confirmation_code}}': 'TEST123',
        '{{reservation_date}}': new Date().toLocaleDateString(),
        '{{seat_label}}': 'Cabana #1',
        '{{amount}}': '$150.00',
        '{{area_name}}': 'Pool Deck'
      };
      
      let body = testingTemplate.body || '';
      let subject = testingTemplate.subject || '';
      
      Object.entries(sampleData).forEach(([key, value]) => {
        body = body.replace(new RegExp(key.replace(/[{}]/g, '\\$&'), 'g'), value);
        subject = subject.replace(new RegExp(key.replace(/[{}]/g, '\\$&'), 'g'), value);
      });

      if (testingTemplate.type === 'email') {
        await axios.post(`${API_URL}/email/test`, {
          email: testRecipient,
          subject: subject || 'Test Email',
          body: body,
          property_id: selectedProperty
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Test email sent successfully');
      } else {
        await axios.post(`${API_URL}/sms/test`, {
          phone: testRecipient,
          message: body,
          property_id: selectedProperty
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Test SMS sent successfully');
      }
      
      setTestTemplateDialogOpen(false);
    } catch (error) {
      console.error('Failed to send test:', error);
      toast.error('Failed to send test: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Weather Alert Handlers
  const openWeatherAlertDialog = (alert = null) => {
    if (alert) {
      setEditingWeatherAlert(alert);
      setWeatherAlertForm({
        alert_type: alert.alert_type,
        threshold_value: alert.threshold_value,
        threshold_unit: alert.threshold_unit,
        comparison: alert.comparison,
        message: alert.message,
        check_interval_minutes: alert.check_interval_minutes,
        cooldown_minutes: alert.cooldown_minutes,
        active: alert.active
      });
    } else {
      setEditingWeatherAlert(null);
      setWeatherAlertForm({
        alert_type: 'wind',
        threshold_value: 15,
        threshold_unit: 'mph',
        comparison: 'gte',
        message: '',
        check_interval_minutes: 30,
        cooldown_minutes: 120,
        active: true
      });
    }
    setWeatherAlertDialogOpen(true);
  };

  const handleSaveWeatherAlert = async () => {
    try {
      const token = localStorage.getItem('token');
      const data = {
        property_id: selectedProperty,
        ...weatherAlertForm
      };
      
      if (editingWeatherAlert) {
        await axios.put(`${API_URL}/weather-alerts/${editingWeatherAlert.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Weather alert updated');
      } else {
        await axios.post(`${API_URL}/weather-alerts`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Weather alert created');
      }
      
      setWeatherAlertDialogOpen(false);
      fetchWeatherAlerts();
    } catch (error) {
      toast.error('Failed to save weather alert');
    }
  };

  const handleDeleteWeatherAlert = async (alertId) => {
    if (!window.confirm('Are you sure you want to delete this alert?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/weather-alerts/${alertId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Weather alert deleted');
      fetchWeatherAlerts();
    } catch (error) {
      toast.error('Failed to delete alert');
    }
  };

  // Legal Page Handlers
  const openLegalPageDialog = (pageType) => {
    setEditingLegalPage(pageType);
    setLegalPageDialogOpen(true);
  };

  const handleSaveLegalPage = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/legal/${editingLegalPage}`, {
        property_id: selectedProperty,
        title: legalPages[editingLegalPage].title,
        content: legalPages[editingLegalPage].content
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Legal page updated');
      setLegalPageDialogOpen(false);
      fetchLegalPages();
    } catch (error) {
      toast.error('Failed to save legal page');
    }
  };

  // Test SMS
  const handleTestSms = async () => {
    if (!testSmsPhone) {
      toast.error('Please enter a phone number');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/sms/test`, {
        phone: testSmsPhone,
        message: 'This is a test message from Acqualina Resort.',
        property_id: selectedProperty
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Test SMS sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test SMS');
    }
  };

  // Test Weather Alert
  const handleTestWeatherAlert = async (alert) => {
    const phone = prompt('Enter phone number to send test alert:', testAlertPhone);
    if (!phone) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/sms/test`, {
        phone: phone,
        message: alert.message,
        property_id: selectedProperty
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Test weather alert sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test alert');
    }
  };

  const handleSendTestWeatherAlert = async () => {
    if (!testAlertPhone) {
      toast.error('Please enter a phone number');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/sms/test`, {
        phone: testAlertPhone,
        message: testAlertMessage,
        property_id: selectedProperty
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Test weather alert sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test alert');
    }
  };

  return (
    <AdminLayout title="Integration Settings">
      <Card className="bg-white border-slate-200 shadow-sm rounded-md mb-6">
        <CardContent className="pt-6">
          <div className="max-w-md">
            <Label htmlFor="property">Select Property</Label>
            <select
              id="property"
              value={selectedProperty}
              onChange={(e) => setSelectedProperty(e.target.value)}
              className="w-full mt-2 p-3 border border-[#E7E5E4] rounded-sm"
              data-testid="select-property"
            >
              {properties.map((prop) => (
                <option key={prop.id} value={prop.id}>{prop.name}</option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="ohip" className="w-full">
        <TabsList className="mb-6 flex-wrap">
          <TabsTrigger value="ohip" data-testid="tab-ohip">Opera Cloud (OHIP)</TabsTrigger>
          <TabsTrigger value="stripe" data-testid="tab-stripe">Stripe</TabsTrigger>
          <TabsTrigger value="twilio" data-testid="tab-twilio">Twilio</TabsTrigger>
          <TabsTrigger value="email" data-testid="tab-email">Email (SMTP2GO)</TabsTrigger>
          <TabsTrigger value="email-templates" data-testid="tab-email-templates">Email Templates</TabsTrigger>
          <TabsTrigger value="sms-templates" data-testid="tab-sms-templates">SMS Templates</TabsTrigger>
          <TabsTrigger value="weather" data-testid="tab-weather">Weather Alerts</TabsTrigger>
          <TabsTrigger value="legal" data-testid="tab-legal">Legal Pages</TabsTrigger>
        </TabsList>

        {/* OHIP Settings */}
        <TabsContent value="ohip">
          <Card className="bg-white border-slate-200 shadow-sm rounded-md">
            <CardHeader>
              <div className="flex items-center gap-3">
                <Key className="w-6 h-6 text-[#9F1239]" />
                <div>
                  <CardTitle className="text-xl font-serif">Opera Cloud OHIP Integration</CardTitle>
                  <CardDescription>Configure hotel guest validation through Oracle Hospitality OHIP API</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="ohip_url">OHIP API URL</Label>
                <Input
                  id="ohip_url"
                  value={settings.ohip_url}
                  onChange={(e) => handleChange('ohip_url', e.target.value)}
                  placeholder="https://your-ohip-instance.oraclecloud.com"
                  data-testid="input-ohip-url"
                />
              </div>
              <div>
                <Label htmlFor="ohip_client_id">Client ID</Label>
                <Input
                  id="ohip_client_id"
                  value={settings.ohip_client_id}
                  onChange={(e) => handleChange('ohip_client_id', e.target.value)}
                  placeholder="Enter your OHIP Client ID"
                  data-testid="input-ohip-client-id"
                />
              </div>
              <div>
                <Label htmlFor="ohip_client_secret">Client Secret</Label>
                <Input
                  id="ohip_client_secret"
                  type="password"
                  value={settings.ohip_client_secret}
                  onChange={(e) => handleChange('ohip_client_secret', e.target.value)}
                  placeholder="Enter your OHIP Client Secret"
                  data-testid="input-ohip-secret"
                />
              </div>
              <div>
                <Label htmlFor="ohip_application_key">Application Key</Label>
                <Input
                  id="ohip_application_key"
                  type="password"
                  value={settings.ohip_application_key}
                  onChange={(e) => handleChange('ohip_application_key', e.target.value)}
                  placeholder="Enter your OHIP Application Key"
                  data-testid="input-ohip-app-key"
                />
              </div>
              <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-sm p-4 mt-4">
                <p className="text-sm text-[#9F1239] font-medium mb-2">How to get OHIP credentials:</p>
                <ol className="text-sm text-[#78716C] list-decimal list-inside space-y-1">
                  <li>Log in to your Oracle Hospitality Integration Platform</li>
                  <li>Navigate to Application Management → Register Application</li>
                  <li>Create a new application to receive Client ID and Client Secret</li>
                  <li>Generate an Application Key from the application settings</li>
                </ol>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Stripe Settings */}
        <TabsContent value="stripe">
          <Card className="bg-white border-slate-200 shadow-sm rounded-md">
            <CardHeader>
              <div className="flex items-center gap-3">
                <CreditCard className="w-6 h-6 text-[#9F1239]" />
                <div>
                  <CardTitle className="text-xl font-serif">Stripe Payment Integration</CardTitle>
                  <CardDescription>Configure payment processing with authorization-only flow</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="stripe_api_key">Stripe Secret Key</Label>
                <Input
                  id="stripe_api_key"
                  type="password"
                  value={settings.stripe_api_key}
                  onChange={(e) => handleChange('stripe_api_key', e.target.value)}
                  placeholder="sk_test_... or sk_live_..."
                  data-testid="input-stripe-key"
                />
                <p className="text-sm text-[#78716C] mt-2">
                  Use test key for development or your live key for production
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Twilio Settings */}
        <TabsContent value="twilio">
          <Card className="bg-white border-slate-200 shadow-sm rounded-md">
            <CardHeader>
              <div className="flex items-center gap-3">
                <MessageSquare className="w-6 h-6 text-[#9F1239]" />
                <div>
                  <CardTitle className="text-xl font-serif">Twilio SMS Integration</CardTitle>
                  <CardDescription>Configure SMS messaging for reservation confirmations and weather alerts</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Twilio Credentials */}
              <div className="space-y-4">
                <h3 className="font-semibold text-[#1C1917] border-b pb-2">API Credentials</h3>
                <div>
                  <Label htmlFor="twilio_account_sid">Account SID</Label>
                  <Input
                    id="twilio_account_sid"
                    value={settings.twilio_account_sid}
                    onChange={(e) => handleChange('twilio_account_sid', e.target.value)}
                    placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    data-testid="input-twilio-sid"
                  />
                </div>
                <div>
                  <Label htmlFor="twilio_auth_token">Auth Token</Label>
                  <Input
                    id="twilio_auth_token"
                    type="password"
                    value={settings.twilio_auth_token}
                    onChange={(e) => handleChange('twilio_auth_token', e.target.value)}
                    placeholder="Enter your Twilio auth token"
                    data-testid="input-twilio-token"
                  />
                </div>
                <div>
                  <Label htmlFor="twilio_phone_number">Twilio Phone Number</Label>
                  <Input
                    id="twilio_phone_number"
                    value={settings.twilio_phone_number}
                    onChange={(e) => handleChange('twilio_phone_number', e.target.value)}
                    placeholder="+1234567890"
                    data-testid="input-twilio-phone"
                  />
                </div>
              </div>
              
              {/* SMS Features */}
              <div className="space-y-4">
                <h3 className="font-semibold text-[#1C1917] border-b pb-2">SMS Features</h3>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="sms_enabled"
                    checked={settings.sms_enabled}
                    onChange={(e) => handleChange('sms_enabled', e.target.checked)}
                    className="w-4 h-4 text-[#9F1239] rounded"
                  />
                  <Label htmlFor="sms_enabled">Enable SMS Messaging</Label>
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="send_confirmation_sms"
                    checked={settings.send_confirmation_sms}
                    onChange={(e) => handleChange('send_confirmation_sms', e.target.checked)}
                    className="w-4 h-4 text-[#9F1239] rounded"
                  />
                  <Label htmlFor="send_confirmation_sms">Send confirmation SMS when reservation is made</Label>
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="send_reminder_sms"
                    checked={settings.send_reminder_sms}
                    onChange={(e) => handleChange('send_reminder_sms', e.target.checked)}
                    className="w-4 h-4 text-[#9F1239] rounded"
                  />
                  <Label htmlFor="send_reminder_sms">Send reminder SMS before reservation</Label>
                </div>
                {settings.send_reminder_sms && (
                  <div>
                    <Label htmlFor="reminder_hours_before">Hours before reservation to send reminder</Label>
                    <Input
                      id="reminder_hours_before"
                      type="number"
                      value={settings.reminder_hours_before}
                      onChange={(e) => handleChange('reminder_hours_before', parseInt(e.target.value))}
                      className="max-w-[150px]"
                    />
                  </div>
                )}
              </div>
              
              {/* Test SMS */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-semibold text-[#1C1917]">Send Test SMS</h3>
                <div className="flex gap-3">
                  <Input
                    placeholder="+1 (555) 123-4567"
                    value={testSmsPhone}
                    onChange={(e) => setTestSmsPhone(e.target.value)}
                    className="max-w-[200px]"
                  />
                  <Button onClick={handleTestSms} variant="outline">
                    Send Test
                  </Button>
                </div>
              </div>
              
              <Button onClick={handleSave} disabled={loading} className="bg-[#9F1239] hover:bg-[#881337]">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save SMS Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SMTP2GO Settings */}
        <TabsContent value="email">
          <Card className="bg-white border-slate-200 shadow-sm rounded-md">
            <CardHeader>
              <div className="flex items-center gap-3">
                <Mail className="w-6 h-6 text-[#9F1239]" />
                <div>
                  <CardTitle className="text-xl font-serif">SMTP2GO Email Integration</CardTitle>
                  <CardDescription>Configure email delivery for booking confirmations and notifications</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="smtp2go_api_key">SMTP2GO API Key</Label>
                <Input
                  id="smtp2go_api_key"
                  type="password"
                  value={settings.smtp2go_api_key}
                  onChange={(e) => handleChange('smtp2go_api_key', e.target.value)}
                  placeholder="api-xxxxxxxxxxxxxxxx"
                  data-testid="input-smtp2go-key"
                />
              </div>
              <div>
                <Label htmlFor="smtp2go_sender_name">Sender Name</Label>
                <Input
                  id="smtp2go_sender_name"
                  value={settings.smtp2go_sender_name}
                  onChange={(e) => handleChange('smtp2go_sender_name', e.target.value)}
                  placeholder="Acqualina Resort"
                  data-testid="input-smtp2go-name"
                />
              </div>
              <div>
                <Label htmlFor="smtp2go_sender_email">Sender Email</Label>
                <Input
                  id="smtp2go_sender_email"
                  type="email"
                  value={settings.smtp2go_sender_email}
                  onChange={(e) => handleChange('smtp2go_sender_email', e.target.value)}
                  placeholder="reservations@yourdomain.com"
                  data-testid="input-smtp2go-email"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Templates */}
        <TabsContent value="email-templates">
          <Card className="bg-white border-slate-200 shadow-sm rounded-md">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="w-6 h-6 text-[#9F1239]" />
                  <div>
                    <CardTitle className="text-xl font-serif">Email Templates</CardTitle>
                    <CardDescription>Customize email content sent to guests</CardDescription>
                  </div>
                </div>
                <Button
                  onClick={() => openTemplateDialog(null, 'email')}
                  className="bg-[#9F1239] hover:bg-[#881337]"
                  data-testid="btn-add-email-template"
                >
                  <Plus className="w-4 h-4 mr-2" /> Add Template
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {emailTemplates.map((template) => (
                  <div
                    key={template.id}
                    className="flex items-center justify-between p-4 border border-[#E7E5E4] rounded-sm hover:bg-[#FAFAF9]"
                  >
                    <div className="flex-1">
                      <h4 className="font-medium text-[#1C1917]">{template.name}</h4>
                      <p className="text-sm text-[#78716C]">{template.description}</p>
                      {template.subject && (
                        <p className="text-xs text-[#A8A29E] mt-1">Subject: {template.subject}</p>
                      )}
                      {template.isDefault && (
                        <span className="inline-block text-xs bg-[#F5F5F4] text-[#78716C] px-2 py-0.5 rounded mt-1">Default</span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openTestTemplateDialog(template, 'email')}
                        data-testid={`btn-test-email-${template.id}`}
                        title="Send test email"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openTemplateDialog(template, 'email')}
                        data-testid={`btn-edit-${template.id}`}
                        title="Edit template"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-[#F5F5F4] border border-[#E7E5E4] rounded-sm p-4 mt-4">
                <p className="text-sm text-[#78716C] font-medium mb-3">Available Template Variables:</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  <div className="text-xs">
                    <code className="bg-white px-2 py-1 rounded border text-[#9F1239]">{"{{guest_name}}"}</code>
                    <span className="text-[#78716C] ml-2">Guest name</span>
                  </div>
                  <div className="text-xs">
                    <code className="bg-white px-2 py-1 rounded border text-[#9F1239]">{"{{confirmation_code}}"}</code>
                    <span className="text-[#78716C] ml-2">Booking code</span>
                  </div>
                  <div className="text-xs">
                    <code className="bg-white px-2 py-1 rounded border text-[#9F1239]">{"{{reservation_date}}"}</code>
                    <span className="text-[#78716C] ml-2">Date</span>
                  </div>
                  <div className="text-xs">
                    <code className="bg-white px-2 py-1 rounded border text-[#9F1239]">{"{{seat_label}}"}</code>
                    <span className="text-[#78716C] ml-2">Seat (A1, B2)</span>
                  </div>
                  <div className="text-xs">
                    <code className="bg-white px-2 py-1 rounded border text-[#9F1239]">{"{{amount}}"}</code>
                    <span className="text-[#78716C] ml-2">Price</span>
                  </div>
                  <div className="text-xs">
                    <code className="bg-white px-2 py-1 rounded border text-[#9F1239]">{"{{area_name}}"}</code>
                    <span className="text-[#78716C] ml-2">Area name</span>
                  </div>
                  <div className="text-xs">
                    <code className="bg-white px-2 py-1 rounded border text-[#9F1239]">{"{{property_name}}"}</code>
                    <span className="text-[#78716C] ml-2">Property</span>
                  </div>
                  <div className="text-xs">
                    <code className="bg-white px-2 py-1 rounded border text-[#9F1239]">{"{{guest_email}}"}</code>
                    <span className="text-[#78716C] ml-2">Email</span>
                  </div>
                  <div className="text-xs">
                    <code className="bg-white px-2 py-1 rounded border text-[#9F1239]">{"{{guest_phone}}"}</code>
                    <span className="text-[#78716C] ml-2">Phone</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SMS Templates */}
        <TabsContent value="sms-templates">
          <Card className="bg-white border-slate-200 shadow-sm rounded-md">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <MessageSquare className="w-6 h-6 text-[#9F1239]" />
                  <div>
                    <CardTitle className="text-xl font-serif">SMS Templates</CardTitle>
                    <CardDescription>Customize SMS messages sent to guests (160 character limit)</CardDescription>
                  </div>
                </div>
                <Button
                  onClick={() => openTemplateDialog(null, 'sms')}
                  className="bg-[#9F1239] hover:bg-[#881337]"
                  data-testid="btn-add-sms-template"
                >
                  <Plus className="w-4 h-4 mr-2" /> Add Template
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {smsTemplates.map((template) => (
                  <div
                    key={template.id}
                    className="flex items-center justify-between p-4 border border-[#E7E5E4] rounded-sm hover:bg-[#FAFAF9]"
                  >
                    <div className="flex-1">
                      <h4 className="font-medium text-[#1C1917]">{template.name}</h4>
                      <p className="text-sm text-[#78716C]">{template.description}</p>
                      {template.body && (
                        <p className="text-xs text-[#A8A29E] mt-1 truncate max-w-md">Preview: {template.body.substring(0, 60)}...</p>
                      )}
                      {template.isDefault && (
                        <span className="inline-block text-xs bg-[#F5F5F4] text-[#78716C] px-2 py-0.5 rounded mt-1">Default</span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openTestTemplateDialog(template, 'sms')}
                        data-testid={`btn-test-sms-${template.id}`}
                        title="Send test SMS"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openTemplateDialog(template, 'sms')}
                        data-testid={`btn-edit-${template.id}`}
                        title="Edit template"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* SMS Variables */}
              <div className="bg-[#F5F5F4] border border-[#E7E5E4] rounded-sm p-4 mt-4">
                <p className="text-sm text-[#78716C] font-medium mb-3">Available Template Variables:</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                  <div className="text-xs">
                    <code className="bg-white px-2 py-1 rounded border text-[#9F1239]">{"{{guest_name}}"}</code>
                  </div>
                  <div className="text-xs">
                    <code className="bg-white px-2 py-1 rounded border text-[#9F1239]">{"{{confirmation_code}}"}</code>
                  </div>
                  <div className="text-xs">
                    <code className="bg-white px-2 py-1 rounded border text-[#9F1239]">{"{{reservation_date}}"}</code>
                  </div>
                  <div className="text-xs">
                    <code className="bg-white px-2 py-1 rounded border text-[#9F1239]">{"{{seat_label}}"}</code>
                  </div>
                  <div className="text-xs">
                    <code className="bg-white px-2 py-1 rounded border text-[#9F1239]">{"{{amount}}"}</code>
                  </div>
                  <div className="text-xs">
                    <code className="bg-white px-2 py-1 rounded border text-[#9F1239]">{"{{area_name}}"}</code>
                  </div>
                </div>
                <p className="text-sm text-[#78716C] font-medium mb-2">SMS Best Practices:</p>
                <ul className="text-xs text-[#A8A29E] list-disc list-inside space-y-1">
                  <li>Keep messages under 160 characters to avoid splitting</li>
                  <li>Include confirmation code for easy reference</li>
                  <li>Use short, clear language</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Weather Settings */}
        <TabsContent value="weather">
          <Card className="bg-white border-slate-200 shadow-sm rounded-md">
            <CardHeader>
              <div className="flex items-center gap-3">
                <Cloud className="w-6 h-6 text-[#9F1239]" />
                <div>
                  <CardTitle className="text-xl font-serif">Weather Alerts</CardTitle>
                  <CardDescription>Configure weather-based SMS alerts for guests with active reservations</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Location Settings */}
              <div className="space-y-4">
                <h3 className="font-semibold text-[#1C1917] border-b pb-2">Location Settings</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <Label htmlFor="weather_latitude">Latitude</Label>
                    <Input
                      id="weather_latitude"
                      value={settings.weather_latitude}
                      onChange={(e) => handleChange('weather_latitude', e.target.value)}
                      placeholder="25.9565"
                    />
                  </div>
                  <div>
                    <Label htmlFor="weather_longitude">Longitude</Label>
                    <Input
                      id="weather_longitude"
                      value={settings.weather_longitude}
                      onChange={(e) => handleChange('weather_longitude', e.target.value)}
                      placeholder="-80.1253"
                    />
                  </div>
                  <div>
                    <Label htmlFor="weather_check_interval_minutes">Check Interval (minutes)</Label>
                    <Input
                      id="weather_check_interval_minutes"
                      type="number"
                      value={settings.weather_check_interval_minutes}
                      onChange={(e) => handleChange('weather_check_interval_minutes', parseInt(e.target.value))}
                      placeholder="60"
                    />
                    <p className="text-xs text-[#78716C] mt-1">Default: 60 min (1 hour)</p>
                  </div>
                  <div>
                    <Label htmlFor="weather_alerts_cutoff_time">No Alerts After (property time)</Label>
                    <Input
                      id="weather_alerts_cutoff_time"
                      type="time"
                      value={settings.weather_alerts_cutoff_time}
                      onChange={(e) => handleChange('weather_alerts_cutoff_time', e.target.value)}
                    />
                    <p className="text-xs text-[#78716C] mt-1">Stop sending alerts after this time</p>
                  </div>
                </div>
                <p className="text-xs text-[#78716C]">
                  Data Source: National Weather Service API (api.weather.gov) - free, no API key required. 
                  Alerts are sent only to guests with active reservations for today who have opted in to SMS.
                </p>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="weather_enabled"
                    checked={settings.weather_enabled}
                    onChange={(e) => handleChange('weather_enabled', e.target.checked)}
                    className="w-4 h-4 text-[#9F1239] rounded"
                  />
                  <Label htmlFor="weather_enabled">Enable Weather Alerts</Label>
                </div>
              </div>
              
              {/* Weather Alerts List */}
              <div className="space-y-4">
                <div className="flex items-center justify-between border-b pb-2">
                  <h3 className="font-semibold text-[#1C1917]">Alert Thresholds</h3>
                  <Button onClick={() => openWeatherAlertDialog()} variant="outline" size="sm">
                    <Plus className="w-4 h-4 mr-2" /> Add Alert
                  </Button>
                </div>
                
                {weatherAlerts.length === 0 ? (
                  <div className="text-center py-8 bg-[#F5F5F4] rounded-sm">
                    <Cloud className="w-12 h-12 text-[#78716C] mx-auto mb-3" />
                    <p className="text-[#78716C]">No weather alerts configured</p>
                    <p className="text-sm text-[#A8A29E]">Add alerts to notify guests about weather conditions</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {weatherAlerts.map((alert) => (
                      <div key={alert.id} className="flex items-center justify-between p-4 bg-[#F5F5F4] rounded-sm border border-[#E7E5E4]">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${alert.active ? 'bg-[#FEF2F2]' : 'bg-gray-200'}`}>
                            {alert.alert_type === 'wind' && <Wind className={`w-5 h-5 ${alert.active ? 'text-[#9F1239]' : 'text-gray-400'}`} />}
                            {alert.alert_type === 'rain' && <CloudRain className={`w-5 h-5 ${alert.active ? 'text-[#9F1239]' : 'text-gray-400'}`} />}
                            {alert.alert_type === 'lightning' && <Zap className={`w-5 h-5 ${alert.active ? 'text-[#9F1239]' : 'text-gray-400'}`} />}
                            {alert.alert_type === 'heat' && <Thermometer className={`w-5 h-5 ${alert.active ? 'text-[#9F1239]' : 'text-gray-400'}`} />}
                          </div>
                          <div>
                            <p className="font-medium text-[#1C1917] capitalize">
                              {alert.alert_type} Alert: {alert.comparison === 'gte' ? '≥' : alert.comparison === 'lte' ? '≤' : '='} {alert.threshold_value} {alert.threshold_unit}
                            </p>
                            <p className="text-sm text-[#78716C] truncate max-w-md">{alert.message}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 text-xs rounded ${alert.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                            {alert.active ? 'Active' : 'Inactive'}
                          </span>
                          <Button variant="ghost" size="sm" onClick={() => openWeatherAlertDialog(alert)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteWeatherAlert(alert.id)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleTestWeatherAlert(alert)}
                            title="Send test alert"
                          >
                            <Send className="w-4 h-4 text-blue-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Test Weather Alert */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-semibold text-[#1C1917]">Test Weather Alert</h3>
                <p className="text-sm text-[#78716C]">
                  Send a test weather alert SMS to verify your Twilio configuration and message formatting.
                </p>
                <div className="flex gap-3 items-end">
                  <div className="flex-1 max-w-xs">
                    <Label htmlFor="test_alert_phone">Phone Number</Label>
                    <Input
                      id="test_alert_phone"
                      placeholder="+1 (555) 123-4567"
                      value={testAlertPhone}
                      onChange={(e) => setTestAlertPhone(e.target.value)}
                    />
                  </div>
                  <div className="flex-1 max-w-md">
                    <Label htmlFor="test_alert_message">Test Message</Label>
                    <Input
                      id="test_alert_message"
                      placeholder="Weather Alert: Umbrellas may not be available..."
                      value={testAlertMessage}
                      onChange={(e) => setTestAlertMessage(e.target.value)}
                    />
                  </div>
                  <Button 
                    onClick={handleSendTestWeatherAlert} 
                    variant="outline"
                    disabled={!testAlertPhone}
                  >
                    <Send className="w-4 h-4 mr-2" /> Send Test Alert
                  </Button>
                </div>
              </div>
              
              <Button onClick={handleSave} disabled={loading} className="bg-[#9F1239] hover:bg-[#881337]">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Weather Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Legal Pages */}
        <TabsContent value="legal">
          <Card className="bg-white border-slate-200 shadow-sm rounded-md">
            <CardHeader>
              <div className="flex items-center gap-3">
                <FileText className="w-6 h-6 text-[#9F1239]" />
                <div>
                  <CardTitle className="text-xl font-serif">Legal Pages</CardTitle>
                  <CardDescription>Manage SMS Terms & Conditions and Privacy Policy pages</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* SMS Terms */}
              <div className="p-4 bg-[#F5F5F4] rounded-sm border border-[#E7E5E4]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <MessageSquare className="w-8 h-8 text-[#9F1239]" />
                    <div>
                      <h4 className="font-medium text-[#1C1917]">SMS Terms and Conditions</h4>
                      <p className="text-sm text-[#78716C]">
                        Required for SMS opt-in compliance. Displayed when guests consent to SMS.
                      </p>
                      <a href="/sms-terms" target="_blank" className="text-xs text-[#9F1239] hover:underline">
                        View public page →
                      </a>
                    </div>
                  </div>
                  <Button variant="outline" onClick={() => openLegalPageDialog('sms_terms')}>
                    <Edit className="w-4 h-4 mr-2" /> Edit
                  </Button>
                </div>
              </div>
              
              {/* Privacy Policy */}
              <div className="p-4 bg-[#F5F5F4] rounded-sm border border-[#E7E5E4]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Shield className="w-8 h-8 text-[#9F1239]" />
                    <div>
                      <h4 className="font-medium text-[#1C1917]">Privacy Policy</h4>
                      <p className="text-sm text-[#78716C]">
                        Describes how guest data is collected, used, and protected.
                      </p>
                      <a href="/privacy-policy" target="_blank" className="text-xs text-[#9F1239] hover:underline">
                        View public page →
                      </a>
                    </div>
                  </div>
                  <Button variant="outline" onClick={() => openLegalPageDialog('privacy_policy')}>
                    <Edit className="w-4 h-4 mr-2" /> Edit
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Template Edit Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-serif">
              {editingTemplate ? 'Edit' : 'Create'} {templateType === 'email' ? 'Email' : 'SMS'} Template
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="template_name">Template Name</Label>
              <Input
                id="template_name"
                value={templateForm.name}
                onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                placeholder="e.g., Booking Confirmation"
              />
            </div>
            {templateType === 'email' && (
              <div>
                <Label htmlFor="template_subject">Email Subject</Label>
                <Input
                  id="template_subject"
                  value={templateForm.subject}
                  onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
                  placeholder="Your Reservation is Confirmed - {{confirmation_code}}"
                />
              </div>
            )}
            <div>
              <Label htmlFor="template_body">
                {templateType === 'email' ? 'Email Body (HTML supported)' : 'SMS Message'}
              </Label>
              <Textarea
                id="template_body"
                value={templateForm.body}
                onChange={(e) => setTemplateForm({ ...templateForm, body: e.target.value })}
                placeholder={templateType === 'email' 
                  ? "Dear {{guest_name}},\n\nYour reservation has been confirmed..."
                  : "AcquaCana: Your reservation {{confirmation_code}} is confirmed for {{reservation_date}}."
                }
                rows={templateType === 'email' ? 10 : 3}
              />
              {templateType === 'sms' && (
                <p className={`text-xs mt-1 ${templateForm.body.length > 160 ? 'text-red-600' : 'text-[#78716C]'}`}>
                  {templateForm.body.length}/160 characters
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="template_description">Description (internal use)</Label>
              <Input
                id="template_description"
                value={templateForm.description}
                onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
                placeholder="When this template is used"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleSaveTemplate}
                className="flex-1 bg-[#9F1239] hover:bg-[#881337]"
              >
                Save Template
              </Button>
              <Button
                variant="outline"
                onClick={() => setTemplateDialogOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Weather Alert Dialog */}
      <Dialog open={weatherAlertDialogOpen} onOpenChange={setWeatherAlertDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-serif">
              {editingWeatherAlert ? 'Edit' : 'Create'} Weather Alert
            </DialogTitle>
            <DialogDescription>
              Configure when to send SMS alerts to guests based on weather conditions
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="alert_type">Alert Type</Label>
              <select
                id="alert_type"
                value={weatherAlertForm.alert_type}
                onChange={(e) => setWeatherAlertForm({ ...weatherAlertForm, alert_type: e.target.value })}
                className="w-full p-2 border border-[#E7E5E4] rounded-sm"
              >
                <option value="wind">Wind Speed</option>
                <option value="rain">Rain Probability</option>
                <option value="lightning">Lightning Risk</option>
                <option value="heat">Heat Index</option>
              </select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label htmlFor="comparison">Condition</Label>
                <select
                  id="comparison"
                  value={weatherAlertForm.comparison}
                  onChange={(e) => setWeatherAlertForm({ ...weatherAlertForm, comparison: e.target.value })}
                  className="w-full p-2 border border-[#E7E5E4] rounded-sm"
                >
                  <option value="gte">≥ Greater or equal</option>
                  <option value="lte">≤ Less or equal</option>
                  <option value="eq">= Equal</option>
                </select>
              </div>
              <div>
                <Label htmlFor="threshold_value">Value</Label>
                <Input
                  id="threshold_value"
                  type="number"
                  value={weatherAlertForm.threshold_value}
                  onChange={(e) => setWeatherAlertForm({ ...weatherAlertForm, threshold_value: parseFloat(e.target.value) })}
                />
              </div>
              <div>
                <Label htmlFor="threshold_unit">Unit</Label>
                <select
                  id="threshold_unit"
                  value={weatherAlertForm.threshold_unit}
                  onChange={(e) => setWeatherAlertForm({ ...weatherAlertForm, threshold_unit: e.target.value })}
                  className="w-full p-2 border border-[#E7E5E4] rounded-sm"
                >
                  <option value="mph">mph</option>
                  <option value="%">%</option>
                  <option value="°F">°F</option>
                </select>
              </div>
            </div>
            <div>
              <Label htmlFor="alert_message">SMS Message to Send</Label>
              <Textarea
                id="alert_message"
                value={weatherAlertForm.message}
                onChange={(e) => setWeatherAlertForm({ ...weatherAlertForm, message: e.target.value })}
                placeholder="e.g., Weather Alert: Umbrellas may not be available today due to high wind conditions. Please visit the concierge for alternatives."
                rows={3}
              />
              <p className="text-xs text-[#78716C] mt-1">Max 160 characters recommended for SMS ({weatherAlertForm.message.length} characters)</p>
              <div className="mt-2 p-2 bg-[#F5F5F4] rounded text-xs">
                <span className="text-[#78716C] font-medium">Available variables: </span>
                <code className="text-[#9F1239]">{"{{wind_speed}}"}</code>,{" "}
                <code className="text-[#9F1239]">{"{{temperature}}"}</code>,{" "}
                <code className="text-[#9F1239]">{"{{rain_chance}}"}</code>,{" "}
                <code className="text-[#9F1239]">{"{{forecast}}"}</code>,{" "}
                <code className="text-[#9F1239]">{"{{property_name}}"}</code>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="check_interval">Check every (minutes)</Label>
                <Input
                  id="check_interval"
                  type="number"
                  value={weatherAlertForm.check_interval_minutes}
                  onChange={(e) => setWeatherAlertForm({ ...weatherAlertForm, check_interval_minutes: parseInt(e.target.value) })}
                />
              </div>
              <div>
                <Label htmlFor="cooldown">Cooldown (minutes)</Label>
                <Input
                  id="cooldown"
                  type="number"
                  value={weatherAlertForm.cooldown_minutes}
                  onChange={(e) => setWeatherAlertForm({ ...weatherAlertForm, cooldown_minutes: parseInt(e.target.value) })}
                />
                <p className="text-xs text-[#78716C] mt-1">Do not re-send same alert within this time</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="alert_active"
                checked={weatherAlertForm.active}
                onChange={(e) => setWeatherAlertForm({ ...weatherAlertForm, active: e.target.checked })}
                className="w-4 h-4 text-[#9F1239] rounded"
              />
              <Label htmlFor="alert_active">Alert is active</Label>
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleSaveWeatherAlert}
                className="flex-1 bg-[#9F1239] hover:bg-[#881337]"
              >
                Save Alert
              </Button>
              <Button
                variant="outline"
                onClick={() => setWeatherAlertDialogOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Legal Page Editor Dialog */}
      <Dialog open={legalPageDialogOpen} onOpenChange={setLegalPageDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-serif">
              Edit {editingLegalPage === 'sms_terms' ? 'SMS Terms and Conditions' : 'Privacy Policy'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="legal_title">Page Title</Label>
              <Input
                id="legal_title"
                value={legalPages[editingLegalPage]?.title || ''}
                onChange={(e) => setLegalPages({
                  ...legalPages,
                  [editingLegalPage]: { ...legalPages[editingLegalPage], title: e.target.value }
                })}
                placeholder="Enter page title"
              />
            </div>
            <div>
              <Label htmlFor="legal_content">Content (HTML supported)</Label>
              <Textarea
                id="legal_content"
                value={legalPages[editingLegalPage]?.content || ''}
                onChange={(e) => setLegalPages({
                  ...legalPages,
                  [editingLegalPage]: { ...legalPages[editingLegalPage], content: e.target.value }
                })}
                placeholder="Enter page content..."
                rows={15}
                className="font-mono text-sm"
              />
              <p className="text-xs text-[#78716C] mt-1">
                You can use HTML tags like &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, etc.
              </p>
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleSaveLegalPage}
                className="flex-1 bg-[#9F1239] hover:bg-[#881337]"
              >
                Save Page
              </Button>
              <Button
                variant="outline"
                onClick={() => setLegalPageDialogOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Test Template Dialog */}
      <Dialog open={testTemplateDialogOpen} onOpenChange={setTestTemplateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-serif">
              Test {testingTemplate?.type === 'email' ? 'Email' : 'SMS'} Template
            </DialogTitle>
            <DialogDescription>
              Send a test {testingTemplate?.type === 'email' ? 'email' : 'SMS'} with sample data
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="test_recipient">
                {testingTemplate?.type === 'email' ? 'Email Address' : 'Phone Number'}
              </Label>
              <Input
                id="test_recipient"
                type={testingTemplate?.type === 'email' ? 'email' : 'tel'}
                value={testRecipient}
                onChange={(e) => setTestRecipient(e.target.value)}
                placeholder={testingTemplate?.type === 'email' ? 'test@example.com' : '+1234567890'}
              />
            </div>
            
            <div className="bg-[#F5F5F4] border border-[#E7E5E4] rounded-sm p-3">
              <p className="text-sm font-medium text-[#1C1917] mb-2">Template: {testingTemplate?.name}</p>
              {testingTemplate?.subject && (
                <p className="text-xs text-[#78716C] mb-1">Subject: {testingTemplate.subject}</p>
              )}
              <p className="text-xs text-[#78716C]">
                {testingTemplate?.body ? 
                  (testingTemplate.body.length > 150 ? testingTemplate.body.substring(0, 150) + '...' : testingTemplate.body) 
                  : 'No body content'}
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-sm p-3">
              <p className="text-xs text-blue-800">
                <strong>Note:</strong> Template variables will be replaced with sample data:
                <br />&#8226; Guest Name: &quot;Test Guest&quot;
                <br />&#8226; Confirmation Code: &quot;TEST123&quot;
                <br />&#8226; Date: Today&apos;s date
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                onClick={handleSendTestTemplate}
                className="flex-1 bg-[#9F1239] hover:bg-[#881337]"
              >
                <Send className="w-4 h-4 mr-2" />
                Send Test
              </Button>
              <Button
                variant="outline"
                onClick={() => setTestTemplateDialogOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <div className="mt-6 flex justify-end">
        <Button
          onClick={handleSave}
          disabled={loading}
          className="bg-[#9F1239] hover:bg-[#881337] text-white px-8 py-6"
          data-testid="btn-save-settings"
        >
          <Save className="mr-2 h-5 w-5" />
          {loading ? 'Saving...' : 'Save All Settings'}
        </Button>
      </div>
    </AdminLayout>
  );
}
